import React, { useEffect, useState } from 'react';
import { Box, Button, Divider, ListItemText, Popover, MenuItem, Typography } from '@mui/material';
import { BlurOn } from '@mui/icons-material';
import chartingMenuSx from './ChartingMenu.styles';
import { useTheme } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { actions } from '../Chart.redux';
import Info from 'components/Info';
import { enqueueSnackbar } from 'notistack';
import SymbolRow from './components/SymbolRow';
import SymbolSearch from './components/SymbolSearch';

const MenuSymbols = () => {
    const theme = useTheme(),
        sx = chartingMenuSx(theme),
        dispatch = useDispatch(),
        selected = useSelector((state) => state.chart.toolbar.selectedButton === 'symbols'),
        tool = useSelector((state) => state.chart.toolbar.selectedTool),
        activeSymbol = useSelector((state) => state.chart.symbolOptions.symbol),
        activeSymbolData = useSelector((state) =>
            activeSymbol !== null ? state.chart.symbols.find((symbol) => symbol.symbol_id === activeSymbol) : null
        ),
        [anchor, setAnchor] = useState(null),
        validTools = ['symbolMulti', 'symbolSingle'],
        [lastToolNotification, setLastToolNotification] = useState(tool || 'symbolMulti'),
        [symbols, setSymbols] = useState([]); // This gets set in SymbolSearch

    console.log({ activeSymbolData, activeSymbol });

    const handleOpen = (e) => {
        setAnchor(e.currentTarget);

        let shouldUpdate = false,
            update = {};

        if (!selected) {
            shouldUpdate = true;
            dispatch(actions.resetSymbolOptions());
            update.selectedButton = 'symbols';
            update.selectedTool = 'symbolMulti';
        }

        if (selected && validTools.includes(tool)) {
            shouldUpdate = false;
        }

        if (selected && !validTools.includes(tool)) {
            shouldUpdate = true;
            update.selectedTool = 'symbolMulti';
        }

        if (shouldUpdate) {
            dispatch(actions.setToolbar(update));
        }
    };

    const setMultiSymbolMode = () => {
        dispatch(
            actions.setToolbar({
                selectedButton: 'symbols',
                selectedTool: 'symbolMulti',
            })
        );

        dispatch(actions.resetSymbolOptions());

        handleClose();
    };

    const setSymbol = (symbolID) => {
        dispatch(
            actions.setToolbar({
                selectedButton: 'symbols',
                selectedTool: 'symbolSingle',
            })
        );

        dispatch(
            actions.resetSymbolOptions({
                symbol: symbolID,
            })
        );

        handleClose();
    };

    const setViewAllCodes = () => {
        dispatch(
            actions.setToolbar({
                showAllSymbolsDialog: true,
            })
        );

        handleClose();
    };

    const handleClose = () => setAnchor(null);

    useEffect(() => {
        if (tool === 'symbolMulti' && lastToolNotification !== 'symbolMulti') {
            enqueueSnackbar(
                <div>
                    Switched to <strong>Multiple Codes Per Tooth</strong> mode
                </div>,
                {
                    variant: 'info',
                    autoHideDuration: 1500,
                }
            );

            setLastToolNotification('symbolMulti');
        }

        if (tool === 'symbolSingle' && lastToolNotification !== 'symbolSingle') {
            enqueueSnackbar(
                <div>
                    Switched to <strong>Multiple Teeth Per Code</strong> mode
                </div>,
                {
                    variant: 'info',
                    autoHideDuration: 1500,
                }
            );

            setLastToolNotification('symbolSingle');
        }
    }, [tool, lastToolNotification, setLastToolNotification]);

    return (
        <>
            <Button
                variant={selected ? 'contained' : 'outlined'}
                color={selected ? 'primary' : undefined}
                sx={{ ...sx.buttonMarginRight, ...sx.buttonFloat }}
                startIcon={<BlurOn />}
                onClick={handleOpen}
            >
                Dx/Tx
            </Button>
            <Popover
                anchorEl={anchor}
                open={Boolean(anchor)}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                slotProps={{
                    paper: {
                        sx: {
                            maxHeight: 'calc(100vh - 150px)',
                            maxWidth: '500px',
                            width: '500px',
                            marginLeft: '-100px',
                        },
                    },
                }}
            >
                <Divider />

                <Box onClick={setMultiSymbolMode} sx={tool === 'symbolMulti' ? sx.activeItem : undefined}>
                    <Box sx={{ padding: 1 }}>
                        {tool === 'symbolMulti' && <strong>Mode: Multiple Codes Per Tooth</strong>}
                        {tool === 'symbolSingle' && (
                            <strong>
                                Mode: Multiple Teeth Per Code (Current: {activeSymbolData.symbol_abbreviation})
                            </strong>
                        )}
                        <Info
                            sx={{ fontSize: '18px', marginLeft: 1 }}
                            tooltipprops={{ onClick: (e) => e?.preventDefault?.() }}
                        >
                            <p>
                                <strong
                                    style={{
                                        color: tool === 'symbolMulti' ? '#84d27a' : undefined,
                                    }}
                                >
                                    Multiple codes per tooth {tool === 'symbolMulti' ? '(active)' : ''}
                                </strong>
                                : You'll be prompted to choose a code each time you click on a tooth or the mouth.
                            </p>

                            <p>
                                <strong
                                    style={{
                                        color: tool === 'symbolSingle' ? '#84d27a' : undefined,
                                    }}
                                >
                                    Multiple Teeth Per Code {tool === 'symbolSingle' ? '(active)' : ''}
                                </strong>
                                : Choose a code from this menu, which can then be placed as many times as necessary
                                without being prompted again.
                            </p>
                        </Info>
                        <br />
                        {tool === 'symbolMulti' && (
                            <Typography>
                                Choose a code below to change to <strong>Multiple Teeth Per Code</strong> mode
                            </Typography>
                        )}
                        {tool === 'symbolSingle' && (
                            <Button variant="contained">Switch to Multiple Codes Per Tooth mode</Button>
                        )}
                    </Box>
                </Box>

                <Divider />

                <SymbolSearch setSymbols={setSymbols} symbols={symbols} handleClose={handleClose} />

                {symbols.map((row) => (
                    <SymbolRow
                        code={row}
                        isActive={tool === 'symbolSingle' && activeSymbol === row.symbol_id}
                        onClick={() => setSymbol(row.symbol_id)}
                        key={row.symbol_id}
                    />
                ))}

                {!symbols.length && (
                    <Typography sx={{ margin: 1 }}>
                        There are no codes to display with your current search settings.
                    </Typography>
                )}
            </Popover>
        </>
    );
};

export default MenuSymbols;
